// Developed by Softvative.com
import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from './AuthContext';

const IncidentContext = createContext();

export const useIncidents = () => {
    const context = useContext(IncidentContext);
    if (!context) {
        throw new Error('useIncidents must be used within an IncidentProvider');
    }
    return context;
};

// Mock custom fields data
const mockCustomFields = [
    { id: 1, name: 'Department', type: 'dropdown', required: true, options: ['IT', 'HR', 'Finance', 'Operations'] },
    { id: 2, name: 'Impact Level', type: 'radiobutton', required: true, options: ['Low', 'Medium', 'High'] },
    { id: 3, name: 'Additional Notes', type: 'multiline', required: false, options: [] },
    { id: 4, name: 'Urgent Request', type: 'checkbox', required: false, options: [] }
];

// Generate mock incidents
const mockIncidents = [
    {
        id: 'INC-1024',
        summary: 'Email server downtime affecting multiple users',
        description: 'Users are reporting inability to send/receive emails since 9:00 AM EST. Exchange server appears to be unresponsive. Affecting approximately 200 users in the organization.',
        category: 'Software',
        subcategory: 'Application',
        service: 'Email Service',
        impact: 'High',
        urgency: 'High',
        priority: 'Critical',
        status: 'In Progress',
        assignedTo: 'Network Team',
        assignee: 'John Smith',
        createdBy: 'Sarah Johnson',
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 30 * 60 * 1000),
        resolvedAt: null,
        tags: ['email', 'server', 'critical'],
        attachments: [],
        customFieldValues: {
            1: 'IT',
            2: 'High',
            3: 'Email server completely down, urgent fix needed',
            4: true
        }
    },
    {
        id: 'INC-1025',
        summary: 'VPN connection issues for remote workers',
        description: 'Multiple remote workers are experiencing intermittent VPN disconnections throughout the day. Issue started after latest firewall update.',
        category: 'Network',
        subcategory: 'VPN',
        service: 'Network Infrastructure',
        impact: 'Medium',
        urgency: 'Medium',
        priority: 'Medium',
        status: 'New',
        assignedTo: 'Network Team',
        assignee: '',
        createdBy: 'Mike Wilson',
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
        resolvedAt: null,
        tags: ['vpn', 'remote', 'firewall'],
        attachments: [],
        customFieldValues: {
            1: 'IT',
            2: 'Medium',
            3: 'VPN disconnections affecting productivity',
            4: false
        }
    },
    {
        id: 'INC-1026',
        summary: 'Database performance degradation',
        description: 'Customer database queries are running significantly slower than usual. Average query time increased from 2 seconds to 45 seconds.',
        category: 'Database',
        subcategory: 'Performance',
        service: 'Database Server',
        impact: 'High',
        urgency: 'Medium',
        priority: 'High',
        status: 'Resolved',
        assignedTo: 'Database Team',
        assignee: 'Emily Chen',
        createdBy: 'David Brown',
        createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        resolvedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        tags: ['database', 'performance', 'query'],
        attachments: [],
        customFieldValues: {
            1: 'IT',
            2: 'High',
            3: 'Database performance severely impacted',
            4: true
        }
    },
    {
        id: 'INC-1027',
        summary: 'Unable to access shared network drives',
        description: 'Users in Finance department cannot access shared network drives. Error message indicates permission issues. Started after recent AD sync.',
        category: 'Access',
        subcategory: 'Permissions',
        service: 'File Server',
        impact: 'Medium',
        urgency: 'Low',
        priority: 'Low',
        status: 'In Progress',
        assignedTo: 'IT Support',
        assignee: 'Alex Johnson',
        createdBy: 'Lisa Wang',
        createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000),
        resolvedAt: null,
        tags: ['file-server', 'permissions', 'finance'],
        attachments: [],
        customFieldValues: {
            1: 'Finance',
            2: 'Low',
            3: 'Finance users unable to access critical files',
            4: false
        }
    },
    {
        id: 'INC-1028',
        summary: 'Printer connectivity issues on third floor',
        description: 'All network printers on third floor are showing as offline. Users cannot print documents. Issue appears to be network-related.',
        category: 'Hardware',
        subcategory: 'Printer',
        service: 'Network Infrastructure',
        impact: 'Low',
        urgency: 'Medium',
        priority: 'Low',
        status: 'New',
        assignedTo: 'Hardware Team',
        assignee: '',
        createdBy: 'Tom Martinez',
        createdAt: new Date(Date.now() - 10 * 60 * 60 * 1000),
        updatedAt: new Date(Date.now() - 10 * 60 * 60 * 1000),
        resolvedAt: null,
        tags: ['printer', 'hardware', 'network'],
        attachments: [],
        customFieldValues: {
            1: 'Operations',
            2: 'Low',
            3: 'Third floor printers all offline',
            4: false
        }
    }
];

export const IncidentProvider = ({ children }) => {
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [customFields, setCustomFields] = useState(mockCustomFields);
    const { user } = useAuth();

    const isDev = import.meta.env.VITE_NODE_ENV === 'development';

    useEffect(() => {
        if (isDev) {
            setIncidents(mockIncidents);
            setCustomFields(mockCustomFields)
        } else {
            if (!user) {
                return;
            }
            fetchIncidents();
            fetchCustomValue();
        }
    }, [user]);

    const fetchIncidents = async () => {
        try {
            setLoading(true);
            const response = await api.get('/incidents');
            if (response.success) {
                setIncidents(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomValue = async () => {
        try {
            setLoading(true);
            const response = await api.get('/custom-fields');
            
            if (response.success) {
                setCustomFields(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const createIncident = async (incidentData) => {
        if (isDev) {
            const newIncident = {
                id: `INC-${String(incidents.length + 1029).padStart(4, '0')}`,
                ...incidentData,
                status: 'New',
                createdAt: new Date(),
                updatedAt: new Date(),
                resolvedAt: null,
                customFieldValues: incidentData.customFieldValues || {}
            };

            setIncidents(prev => [newIncident, ...prev]);
            return newIncident;
        } else {
           try {
                setLoading(true);
                console.log("Create Incident payload: ", incidentData);
                
                const response = await api.post('/incidents', incidentData);
                if (response.success) {
                    return response.data;
                }
            } catch (error) {
                console.error('Failed to create incident:', error);
                toast.error('Failed to create incident');
            } finally {
                setLoading(false);
            }
        }
    };

    const updateIncident = async (id, updates) => {
        if (isDev) {
            setIncidents(prev =>
                prev.map(incident =>
                    incident.id === id
                        ? { ...incident, ...updates, updatedAt: new Date() }
                        : incident
                )
            );
        } else {
            try {
                setLoading(true);
                const response = await api.put(`/incidents/${id}`, updates);
                console.log('Update incident response:', response);
                if (response.success) {
                    setIncidents(prev =>
                        prev.map(item =>
                            item.id === response.data.id ? response.data : item
                        )
                    );
                }
            } catch (error) {
                console.error('Failed to update incident status:', error);
                toast.error('Failed to update incident status');
            } finally {
                setLoading(false);
            }
        }
    };

    const updateIncidentStatus = async (id, status) => {
        if (isDev) {
            setIncidents(prev =>
                prev.map(incident => {
                    if (incident.id === id) {
                        const updates = { status, updatedAt: new Date() };
                        if (status === 'Resolved') {
                            updates.resolvedAt = new Date();
                        }
                        return { ...incident, ...updates };
                    }
                    return incident;
                })
            );
        } else {
            try {
                setLoading(true);
                const payload = { "status": status };
                const response = await api.put(`/incidents/${id}`, payload);
                console.log('Update incident response:', response);
                if (response.success) {
                    setIncidents(prev =>
                        prev.map(item =>
                            item.id === response.data.id ? response.data : item
                        )
                    );
                }
            } catch (error) {
                console.error('Failed to update incident status:', error);
                toast.error('Failed to update incident status');
            } finally {
                setLoading(false);
            }
        }
    };

    const addComment = async (incidentId, comment) => {
        if (isDev) {
            const newComment = {
                id: uuidv4(),
                text: comment,
                author: 'Current User',
                createdAt: new Date()
            };

            updateIncident(incidentId, {
                comments: [
                    ...(incidents.find(i => i.id === incidentId)?.comments || []),
                    newComment
                ]
            });

            return newComment;
        } else {
            const response = await fetch(`/api/incidents/${incidentId}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: comment })
            });

            return await response.json();
        }
    };

    // Custom field management functions
    const addCustomField = (field) => {
        const newField = {
            id: customFields.length + 1,
            ...field
        };
        setCustomFields(prev => [...prev, newField]);
        return newField;
    };

    const updateCustomField = (id, updates) => {
        setCustomFields(prev =>
            prev.map(field =>
                field.id === id ? { ...field, ...updates } : field
            )
        );
    };

    const deleteCustomField = (id) => {
        setCustomFields(prev => prev.filter(field => field.id !== id));
    };

    // Get custom field value for an incident
    const getCustomFieldValue = (incident, custom_field_id) => {
        if (!incident?.customFields) return ''; // no custom fields

        const field = incident.customFields.find(f => f.custom_field_id === custom_field_id);
        if (!field) return ''; // field not found

        // handle type formatting if needed
        switch (field.type) {
            case 'boolean':
                return field.value ? 'Yes' : 'No';
            case 'date':
                return field.value ? new Date(field.value).toLocaleDateString() : '';
            default:
                return field.value ?? '';
        }
    };

    // Calculate statistics
    const getStatistics = () => {
        if (!Array.isArray(incidents)) return {};

        const total = incidents.length;
        const critical = incidents.filter(i => i.priority === 'Critical').length;
        const high = incidents.filter(i => i.priority === 'High').length;
        const newIncidents = incidents.filter(i => i.status === 'New').length;
        const inProgress = incidents.filter(i => i.status === 'In Progress').length;
        const resolved = incidents.filter(i => i.status === 'Resolved').length;

        const overdue = incidents.filter(i => {
            const slaHours =
                i.priority === 'Critical' ? 1 :
                i.priority === 'High' ? 4 :
                i.priority === 'Medium' ? 8 : 24;

            const createdAt = new Date(i.createdAt); // convert string to Date
            const slaDeadline = new Date(createdAt.getTime() + slaHours * 60 * 60 * 1000);
            return new Date() > slaDeadline && i.status !== 'Resolved';
        }).length;

        const resolvedIncidents = incidents.filter(i => i.resolvedAt);
        const avgResolutionTimeMs = resolvedIncidents.reduce((acc, i) => {
            const createdAt = new Date(i.createdAt);
            const resolvedAt = new Date(i.resolvedAt);
            return acc + (resolvedAt.getTime() - createdAt.getTime());
        }, 0);

        const avgResolutionTimeHours = resolvedIncidents.length
            ? Math.round(avgResolutionTimeMs / (resolvedIncidents.length * 1000 * 60 * 60))
            : 0;

        return {
            total,
            critical,
            high,
            newIncidents,
            inProgress,
            resolved,
            overdue,
            avgResolutionTime: avgResolutionTimeHours, // in hours
        };
    };

    const value = {
        incidents,
        customFields,
        createIncident,
        updateIncident,
        updateIncidentStatus,
        addComment,
        getCustomFieldValue,
        getStatistics,
        addCustomField,
        updateCustomField,
        deleteCustomField,
        fetchIncidents,
    };

    return (
        <IncidentContext.Provider value={value}>
            {children}
        </IncidentContext.Provider>
    );
};

export default IncidentContext;